import { CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addColorPalettes,
  updateColorPalette,
} from "../../../../redux/reducers/colorPalette";
import { getPaletteColors } from "../../../../redux/selectors/colorPalette";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { generateColorsFromPrompt } from "../../../../services/api";
import { getRandomId } from "../../../../utils/helpers";
import Button from "../../../Button";
import Input from "../../../Input";
import PaletteItem from "./PaletteItem";
import { PALETTE_TYPES } from "./constants";
import { PALETTE_MODAL_V2_ENABLED } from "../../../../constants/featureFlags";
import { PaletteError, PaletteErrorText, PaletteRetryButton } from "./styles";

const Prompt = ({ paletteType}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const selectedArc = useSelector(getSelectedSegment);
  const paletteColors = useSelector((state) =>
    getPaletteColors(state, "prompt", paletteType)
  );
  const [prompt, setPrompt] = useState(paletteColors[0]?.prompt || "");
  const dispatch = useDispatch();

  function transformPaletteData(data, prompt, previousPalette) {
    return data.map((item, i) => ({
      index: i,
      paletteName: item.palette_name,
      prompt,
      colors: item.colors.map((color, ind) => ({
        hex: color,
        id: getRandomId(),
        selected: false,
        locked:
          i == previousPalette?.index
            ? previousPalette?.colors[ind]?.locked
            : false,
        index: ind,
      })),
      id: getRandomId(),
      selected: i === 0, // Set the first palette as selected
    }));
  }

  const handleGenerateSchemes = async (palette) => {
    const hexes = palette
      ? palette.colors?.map((color) => (color.locked ? color.hex : null))
      : paletteType == PALETTE_TYPES.WHEEL
      ? Array(20).fill(null)
      : Array(5).fill(null);

    const shouldPaletteUpdate = palette
      ? palette.colors?.some((color) => color.locked)
      : false;

    if (!prompt.trim()) {
      setError("Prompt cannot be empty!");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await generateColorsFromPrompt({
        count: paletteType == "wheel" ? 20 : 5,
        prompt,
        hex: hexes,
        scheme_count: 1,
      });

      if (response.data.success) {
        const transformedPalettes = transformPaletteData(
          response.data.data,
          prompt,
          palette
        );

        if (shouldPaletteUpdate) {
          dispatch(
            updateColorPalette({
              paletteType,
              type: "prompt",
              paletteId: palette.id,
              updatedPalette: transformedPalettes[0],
            })
          );
        } else {
          dispatch(
            addColorPalettes({
              paletteType,
              type: "prompt",
              prompt,
              paletteColors: transformedPalettes,
            })
          );
        }
        // Dispatch action to update Redux store
      } else {
        setError("Failed to generate colors from the prompt.");
      }
    } catch (err) {
      console.error("Error generating colors:", err);
      setError("An error occurred while generating colors.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 100px",
          gridGap: 20,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Input
          variant={"secondary"}
          type="textarea"
          placeholder="Enter a subject, theme, or mood to generate a palette."
          inputStyle={{ marginTop: 0 }}
          containerStyle={{ marginBottom: 0 }}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <Button
          variant={"outlined"}
          title="Generate"
          onClick={()=>handleGenerateSchemes()}
        >
          Generate
        </Button>
      </div>

      {PALETTE_MODAL_V2_ENABLED && error ? (
        <PaletteError role="alert">
          <PaletteErrorText>{error}</PaletteErrorText>
          <PaletteRetryButton
            type="button"
            title="Retry"
            onClick={() => handleGenerateSchemes()}
          >
            Retry
          </PaletteRetryButton>
        </PaletteError>
      ) : null}

      <div style={{ marginTop: 20 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop:"10%"
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          paletteColors.map((item, index) => (
            <PaletteItem
              palette={item}
              index={index}
              segmentId={selectedArc?.id}
              handleGenerateSchemes={handleGenerateSchemes}
              type="prompt"
              paletteType={paletteType}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Prompt;
